import { toValue } from "vue";
import { trimToUndefined } from "#ce/utils/props";

/**
 * Pure helpers for the "distance sort needs a location" prompt:
 *   - shouldPromptFor(value) — true when value is "distance" and no location is set
 *   - promptForLocation() — opens the near filter, scrolling it into view
 *   - isLocationResolutionEvent(event, currentSortValue) — true when a document
 *     change event represents the user filling in a location while distance is
 *     active (caller decides what to do: typically close the sort surface)
 *
 * No internal listeners or lifecycle hooks; the caller wires everything up.
 *
 * @param {import("vue").MaybeRefOrGetter<string>} nearFilterSelectorRef
 */
export function useDistanceSortPrompt(nearFilterSelectorRef) {
  function findNearFilter() {
    return document.querySelector(toValue(nearFilterSelectorRef));
  }

  function hasLocationValue(near, lat, lng) {
    return Boolean(trimToUndefined(near) || (trimToUndefined(lat) && trimToUndefined(lng)));
  }

  function hasLocationInParams(params) {
    return hasLocationValue(params.get("near"), params.get("lat"), params.get("lng"));
  }

  function hasLocationInUrl() {
    return hasLocationInParams(new URLSearchParams(window.location.search));
  }

  function hasLocationInNearFilterValue() {
    const filterForm = findNearFilter()?.closest("form");
    if (!filterForm) {
      return false;
    }

    return hasLocationInParams(new FormData(filterForm));
  }

  function hasLocationForDistanceSort() {
    return hasLocationInNearFilterValue() || hasLocationInUrl();
  }

  function shouldPromptFor(value) {
    return value === "distance" && !hasLocationForDistanceSort();
  }

  function promptForLocation() {
    const nearFilter = findNearFilter();
    if (!nearFilter) {
      return;
    }

    nearFilter.open = false;
    requestAnimationFrame(() => {
      nearFilter.open = true;
      nearFilter.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    });
  }

  function isLocationResolutionEvent(event, currentSortValue) {
    if (currentSortValue !== "distance") {
      return false;
    }

    const nearFilter = findNearFilter();
    if (!nearFilter) {
      return false;
    }

    const changedInsideNearFilter = event.composed
      ? event.composedPath().includes(nearFilter)
      : nearFilter.contains(event.target);

    return changedInsideNearFilter && hasLocationInNearFilterValue();
  }

  return { shouldPromptFor, promptForLocation, isLocationResolutionEvent };
}
